import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import usePagination from '../../../../shared/hooks/usePagination';
import { InfluencerInvoiceService } from '../service';
import { Campaign } from '../../../../model/campaign/Campaign';
import { ErrorToast } from '../../../../utils/toasters';
import { Pagination } from '../../../../shared/components/Pagination';
import EmptyState from '../../../../shared/components/placeholder/EmptyState';
import { SearchInput } from '../../../../shared/components/SearchInput';
import InvoiceMainCard from '../components/InvoiceMainCard';

import InvoiceArchiveHeader, { IArchiveHeader } from './components/InvoiceArchiveHeader';
import { useInvoices } from '../../../../shared/hooks/useInvoices';
import { InfluencerOfferModal } from '../../offer/modal/offer-modal/InfluencerOfferModal';
import { InfluencerContractModal } from '../../offer/modal/contract-modal/InfluencerContractModal';
import ModalUploadInvoice from '../components/ModalUploadInvoice';
import { ConfirmationModal } from '../../../../shared/components/modal/ConfirmationModal';
import InvoiceSubCard from '../components/InvoiceSubCard';
import InvoiceSubCardSkeleton from '../components/InvoiceSubCardSkeleton';
import { InvoiceMainCardLoadingSkeleton } from '../../../agency/invoices/components/InvoiceMainCardLoadingSkeleton';
import { StoreKeeper } from '../../../../store/store';
import { loadingSlice } from '../../../../store/general/loadingSlice';

const SKELETON_COUNT = 5;

const initialUploadModalData = {
    show: false,
    rejectReason: undefined,
    sequenceNumber: undefined,
    invoiceId: undefined,
    invoiceFile: undefined,
};

const InvoiceArchive = () => {
    const { filterParams, handlePages, setFilterParams, setTotalPages, setCount, totalPages } = usePagination({
        perPage: 10,
    });
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [expandedCampaignIds, setExpandedCampaignIds] = useState<Set<number>>(new Set());
    const [archiveCampaigns, setArchiveCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaignDetails, setSelectedCampaignDetails] = useState({
        isLongTermOffer: false,
        campaignId: 0,
        title: '',
    });
    const [invoicesTotals, setInvoicesTotals] = useState<IArchiveHeader>({
        totalEarnings: 0,
        outstandingEarnings: 0,
        activeCampaigns: 0,
        completedCampaigns: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const lastQueryKeyRef = useRef<string>();
    const { t } = useTranslation();

    const {
        uploadModalData,
        setUploadModalData,
        showDeleteModal,
        setShowDeleteModal,
        handleSubmitUpload,
        deleteInvoice,
        downloadInvoice,
        isUploading,
        isDeleting,
    } = useInvoices({
        t,
        filterParams,
        setFilterParams,
        isArchivedInvoice: true,
    });

    useEffect(() => {
        // Skelet samo kad se promijeni stranica ili pretraga;
        // osvježavanje nakon uploada ili brisanja (useInvoices) ide tiho
        const queryKey = JSON.stringify({
            page: filterParams.page,
            perPage: filterParams.perPage,
            title: (filterParams as any).title ?? '',
        });
        if (lastQueryKeyRef.current !== queryKey) {
            lastQueryKeyRef.current = queryKey;
            setIsLoading(true);
        }

        let isCurrent = true;
        InfluencerInvoiceService.getInvoiceArchive({ ...filterParams })
            .then(response => {
                if (!isCurrent) return;
                setArchiveCampaigns(response.data);
                setCount(response.count);
                setInvoicesTotals(response.info);
                setTotalPages(Math.ceil(response.count / response.perPage));
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (!isCurrent) return;
                setIsLoading(false);
                setHasLoadedOnce(true);
            });

        return () => {
            isCurrent = false;
        };
    }, [filterParams]);

    const handleSearch = (value: string) => {
        setFilterParams((prev: any) => ({ ...prev, title: value, page: 1 }));
    };

    const openUploadModal = (
        invoiceId: number,
        sequenceNumber: number,
        file?: string,
        rejectReason?: string,
        isLongTermOffer?: boolean,
        selectedCampaignId?: number,
        numberInvoices?: number,
        campaignTitle?: string
    ) => {
        setUploadModalData(prevState => ({
            ...prevState,
            show: true,
            sequenceNumber,
            invoiceId,
            invoiceFile: file,
            rejectReason,
            isLongTermOffer,
            selectedCampaignId,
            invoiceLength: numberInvoices,
            campaignTitle,
        }));
    };

    const openModalAction = (
        isContractModal: boolean,
        isLongTermOffer?: boolean,
        campaignId?: number,
        title?: string
    ) => {
        if (isContractModal) {
            setShowContractModal(true);
        } else {
            setShowOfferModal(true);
        }
        setSelectedCampaignDetails({
            isLongTermOffer: isLongTermOffer ?? false,
            campaignId: campaignId ?? 0,
            title: title ?? '',
        });
    };

    const toggleExpanded = (id: number) => {
        setExpandedCampaignIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    useLayoutEffect(() => {
        StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    });
    return (
        <>
            <InfluencerOfferModal
                show={showOfferModal}
                setShowModal={setShowOfferModal}
                campaignId={String(selectedCampaignDetails?.campaignId ?? 0)}
                isLongTerm={selectedCampaignDetails.isLongTermOffer}
            />
            <InfluencerContractModal
                show={showContractModal}
                setShowModal={setShowContractModal}
                campaignId={String(selectedCampaignDetails.campaignId)}
            />
            <ModalUploadInvoice
                closeModal={() => setUploadModalData(initialUploadModalData)}
                rejectReason={uploadModalData?.rejectReason}
                invoiceFile={uploadModalData?.invoiceFile}
                show={uploadModalData.show}
                handleSubmit={handleSubmitUpload}
                isLoading={isUploading}
                sequenceNumber={uploadModalData.sequenceNumber}
                invoiceLength={uploadModalData.invoiceLength}
                campaignTitle={uploadModalData.campaignTitle}
            />
            <ConfirmationModal
                title={t('influencer.invoice.modal.title')}
                description={t('influencer.invoice.modal.desc', {
                    campaignName: showDeleteModal?.title,
                })}
                action={deleteInvoice}
                show={showDeleteModal.show}
                closeModal={() => setShowDeleteModal(prev => ({ ...prev, show: false, title: '' }))}
                isLoading={isDeleting}
            />

            <Row className={totalPages === 1 ? 'mb-5' : ''}>
                <InvoiceArchiveHeader invoicesTotals={invoicesTotals} isLoading={!hasLoadedOnce} />
                <h6>{t('influencer.invoice.invoice_overview')}</h6>
                <Col sm={6} lg={4} xl={3} className="mb-2">
                    <SearchInput
                        name="title"
                        defaultValue={(filterParams as any).title ?? ''}
                        onSearch={handleSearch}
                        isLoading={isLoading}
                        label={t('general.button.search')}
                        debounceMs={400}
                    />
                </Col>

                {isLoading ? (
                    Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <Col key={i} className="col-12 mt-4">
                            <InvoiceMainCardLoadingSkeleton />
                        </Col>
                    ))
                ) : archiveCampaigns.length ? (
                    archiveCampaigns.map(campaign => {
                        const isExpanded = expandedCampaignIds.has(campaign?.id ?? 0);
                        return (
                            <React.Fragment key={campaign?.offers?.[0]?.id ?? campaign?.id}>
                                <Col className="col-12 mt-4" onClick={() => toggleExpanded(campaign.id)}>
                                    <InvoiceMainCard
                                        selectedCampaign={campaign}
                                        downloadInvoice={downloadInvoice}
                                        openUploadModal={openUploadModal}
                                        openDeleteModal={setShowDeleteModal}
                                        openModalAction={openModalAction}
                                        expended={isExpanded}
                                    />
                                </Col>

                                {/* Long-term fakture: dozvoljene kao prave kartice, buduće kao zaključane */}
                                {isExpanded &&
                                    campaign?.offers?.[0]?.invoiceSchedules?.map((invoice, _i, arr) => (
                                        <Col xs={12} key={invoice.id}>
                                            {invoice?.isAllowed ? (
                                                <InvoiceSubCard
                                                    openUploadModal={openUploadModal}
                                                    invoice={invoice}
                                                    length={arr.length}
                                                    openDeleteModal={setShowDeleteModal}
                                                    downloadInvoice={downloadInvoice}
                                                    selectedCampaignId={campaign?.id}
                                                    selectedCampaignTitle={campaign?.title}
                                                />
                                            ) : (
                                                <InvoiceSubCardSkeleton
                                                    length={arr.length}
                                                    dueDate={invoice?.dueDate}
                                                    sequenceNumber={invoice?.sequenceNumber}
                                                />
                                            )}
                                        </Col>
                                    ))}
                            </React.Fragment>
                        );
                    })
                ) : (
                    <EmptyState
                        desc={t('influencer.invoice.empty_desc')}
                        addClass="col-12 text-center mt-5"
                        title={t('influencer.invoice.empty_title')}
                        iconPath="/icons/profile/invoice.svg"
                        btnText={t('influencer.invoice.chose_campaign')}
                        navigateTo={'/influencer/invoices'}
                        variant="primary"
                    />
                )}

                {!isLoading && totalPages > 1 && (
                    <div className="d-flex justify-content-end mb-md-4">
                        <Pagination page={filterParams.page} totalPages={totalPages} handlePagination={handlePages} />
                    </div>
                )}
            </Row>
        </>
    );
};

export default InvoiceArchive;
